// Copyright 2025 The Casdoor Authors. All Rights Reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React from "react";

// Content for the "用户服务协议" and "隐私政策" dialogs shown on the custom login page.
// The block data below is transcribed verbatim from the source documents
// 《云枢智能体用户服务协议》 and 《云枢智能体隐私政策》 (both发布/生效 2026-08-07). Block types map to the
// document's own styles: h2 = 一/二/三 section headings, h3 = 2.1/4.1 subsection headings, lead = the
// leading paragraph under a heading, p = body paragraph, li / li2 = first / second level list items.
// The block *text* is transcribed from the .docx; a few blocks additionally carry manual formatting
// overrides not present in the source (strong: bold-black emphasis; t: "ol" numbered items; t: "contact"
// line-broken contact card with blue email/phone links). Preserve those overrides if you re-transcribe.

export const TERMS_TITLE = "云枢智能体用户服务协议";
export const PRIVACY_TITLE = "云枢智能体隐私政策";
export const AGREEMENT_UPDATED_AT = "发布日期：2026年8月7日　生效日期：2026年8月7日";

const headingStyle = {
  margin: "22px 0 8px",
  fontSize: 15,
  fontWeight: 800,
  color: "#0f172a",
};
const subHeadingStyle = {
  margin: "16px 0 6px",
  fontSize: 14,
  fontWeight: 700,
  color: "#1e293b",
};
const leadStyle = {
  margin: "0 0 8px",
  fontSize: 13,
  lineHeight: 1.9,
  fontWeight: 500,
  color: "#334155",
};
const paragraphStyle = {
  margin: "0 0 8px",
  fontSize: 13,
  lineHeight: 1.9,
  color: "#475569",
};
const listStyle = {
  margin: "0 0 8px",
  paddingLeft: 22,
  fontSize: 13,
  lineHeight: 1.9,
  color: "#475569",
};
// All bullets use a solid black disc (see .pa-agreement li::marker in ProcurementLoginPage.css).
const listItemStyle = {marginBottom: 2, listStyleType: "disc"};
const subListItemStyle = {marginBottom: 2, marginLeft: 16, listStyleType: "disc"};
// Bold-black emphasis paragraph (used for a few key clauses called out in the source document).
const emphasisStyle = {
  margin: "0 0 8px",
  fontSize: 13,
  lineHeight: 1.9,
  fontWeight: 700,
  color: "#0f172a",
};
const contactLineStyle = {
  margin: "0 0 4px",
  fontSize: 13,
  lineHeight: 1.9,
  color: "#475569",
};
const contactLinkStyle = {color: "#2563eb", fontWeight: 500};

function renderContact(items) {
  return items.map((it, i) => {
    let value = it.value;
    if (it.type === "email") {
      value = <a href={`mailto:${it.value}`} style={contactLinkStyle}>{it.value}</a>;
    } else if (it.type === "phone") {
      value = <a href={`tel:${it.value}`} style={contactLinkStyle}>{it.value}</a>;
    }
    return <p key={i} style={contactLineStyle}>{it.label}{value}</p>;
  });
}

// Render a flat block list, grouping consecutive list items into a single <ul>/<ol>.
function renderBlocks(blocks) {
  const out = [];
  let bucket = [];
  let bucketKind = null; // "ul" | "ol"
  const flush = () => {
    if (bucket.length === 0) {
      return;
    }
    const items = bucket.map((b, i) => (
      <li key={i} style={b.t === "li2" ? subListItemStyle : listItemStyle}>{b.c}</li>
    ));
    if (bucketKind === "ol") {
      out.push(<ol key={`ol-${out.length}`} style={listStyle}>{items}</ol>);
    } else {
      out.push(<ul key={`ul-${out.length}`} style={listStyle}>{items}</ul>);
    }
    bucket = [];
    bucketKind = null;
  };
  blocks.forEach((b, idx) => {
    const kind = b.t === "ol" ? "ol" : (b.t === "li" || b.t === "li2") ? "ul" : null;
    if (kind !== null) {
      if (bucketKind !== null && bucketKind !== kind) {
        flush();
      }
      bucketKind = kind;
      bucket.push(b);
      return;
    }
    flush();
    if (b.t === "h2") {
      out.push(<h4 key={idx} style={headingStyle}>{b.c}</h4>);
    } else if (b.t === "h3") {
      out.push(<h5 key={idx} style={subHeadingStyle}>{b.c}</h5>);
    } else if (b.t === "contact") {
      out.push(<div key={idx}>{renderContact(b.items)}</div>);
    } else if (b.strong) {
      out.push(<p key={idx} style={emphasisStyle}>{b.c}</p>);
    } else if (b.t === "lead") {
      out.push(<p key={idx} style={leadStyle}>{b.c}</p>);
    } else {
      out.push(<p key={idx} style={paragraphStyle}>{b.c}</p>);
    }
  });
  flush();
  return out;
}

const TERMS_BLOCKS = [
  {t: "p", c: "欢迎您使用“云枢智能体”平台及相关服务。"},
  {t: "p", c: "本《云枢智能体用户服务协议》（以下简称“本协议”）是您与北京鼎函云枢科技有限公司（以下简称“我们”或“平台”）之间，就您注册、登录、访问和使用云枢智能体平台及相关产品、功能和服务所订立的协议。"},
  {t: "p", c: "在注册或使用云枢智能体前，请您认真阅读并充分理解本协议，特别是涉及服务限制、用户责任、知识产权、付费服务、免责及责任限制等内容。"},
  {t: "p", strong: true, c: "当您点击“注册”“登录”“同意”“立即使用”等按钮，或实际使用云枢智能体服务时，即表示您已阅读、理解并同意接受本协议的全部内容。"},
  {t: "p", c: "如果您代表企业、机构或其他组织注册或使用云枢智能体，则您确认您已获得相应组织的合法授权，并有权代表该组织接受本协议。"},
  {t: "h2", c: "一、协议范围"},
  {t: "ol", c: "本协议适用于您使用云枢智能体网站、客户端、移动端、API以及平台后续提供的其他相关产品和服务。"},
  {t: "ol", c: "云枢智能体可能根据实际业务提供包括但不限于："},
  {t: "li2", c: "AI智能问答；"},
  {t: "li2", c: "智能体创建与使用；"},
  {t: "li2", c: "文档生成、分析、总结、改写；"},
  {t: "li2", c: "方案、标书、报告、PPT等内容辅助生成；"},
  {t: "li2", c: "文件上传、解析与处理；"},
  {t: "li2", c: "企业知识库及资料检索；"},
  {t: "li2", c: "技能（Skill）调用；"},
  {t: "li2", c: "第三方工具、开放API及数据服务调用；"},
  {t: "li2", c: "互联网信息检索；"},
  {t: "li2", c: "工作流及自动化任务；"},
  {t: "li2", c: "智能点及相关付费服务；"},
  {t: "li2", c: "平台未来新增的其他人工智能服务。"},
  {t: "lead", c: "具体功能以平台实际提供为准。"},
  {t: "h2", c: "二、账户注册与使用"},
  {t: "h3", c: "2.1 注册资格"},
  {t: "lead", c: "您确认，在注册和使用云枢智能体时，您具有与您的行为相适应的民事行为能力。"},
  {t: "p", c: "如果您代表企业或其他组织使用本服务，应确保您具有合法有效的授权。"},
  {t: "h3", c: "2.2 账户信息"},
  {t: "lead", c: "您应按照平台要求提供真实、准确、完整、有效的注册信息，并及时更新。"},
  {t: "p", c: "因您提交的信息不真实、不准确或失效导致账户无法使用、权益受损或产生其他损失的，相应责任由您依法承担。"},
  {t: "h3", c: "2.3 账户安全"},
  {t: "lead", c: "您的账户仅限您本人或获得合法授权的人员使用。"},
  {t: "p", c: "您应妥善保管账户、密码、验证码及其他身份认证信息，不得出租、出售、转让、出借或以其他方式允许未经授权的第三方使用。"},
  {t: "p", c: "如发现账户存在异常登录、信息泄露或未经授权使用等情形，请及时联系我们。"},
  {t: "h3", c: "2.4 企业账户"},
  {t: "lead", c: "对于企业、机构等组织用户，平台可能支持管理员统一管理成员账户、角色、权限、智能点额度、知识库、智能体及相关资源。"},
  {t: "p", c: "企业管理员根据企业授权实施的成员管理、权限调整、账户停用等行为，视为相应企业用户的管理行为。"},
  {t: "p", c: "企业成员如对相关管理操作存在异议，应优先与所属企业管理员沟通。"},
  {t: "h2", c: "三、平台服务"},
  {t: "lead", c: "云枢智能体通过人工智能模型、知识库、软件工具、第三方接口及相关技术，为用户提供智能化内容生成和任务处理服务。"},
  {t: "p", c: "由于人工智能技术具有概率性和不确定性，平台生成或提供的信息可能存在错误、不完整、过时、遗漏或与实际情况不一致的情形。"},
  {t: "p", c: "因此："},
  {t: "li2", c: "AI生成内容仅作为辅助信息和工作效率工具；"},
  {t: "li2", c: "用户应根据具体使用场景自行判断、复核和确认；"},
  {t: "li2", c: "对于投标、合同、法律、财务、医疗、投资、政策判断以及其他可能产生重大影响的事项，应由具有相应专业能力的人员进行最终审核和决策；"},
  {t: "li2", c: "用户不得将AI生成结果直接视为平台作出的专业承诺、事实保证或决策意见。"},
  {t: "lead", c: "对于招投标、采购、销售方案、商务文件等业务场景，平台可以提供内容生成、分析、检查和辅助判断能力，但最终文件内容及业务决策由用户自行确认。"},
  {t: "h2", c: "四、智能点及付费服务"},
  {t: "h3", c: "4.1 智能点"},
  {t: "lead", c: "“智能点”是云枢智能体平台用于衡量和结算部分AI生成、模型调用、文件处理、技能执行及其他计算资源消耗的服务计量单位。"},
  {t: "p", c: "不同模型、智能体、技能或任务可能消耗不同数量的智能点，具体以平台页面展示为准。"},
  {t: "h3", c: "4.2 智能点使用"},
  {t: "lead", c: "使用部分功能时，平台可能根据实际资源消耗扣除相应智能点。"},
  {t: "p", c: "当账户智能点余额不足时，部分需要消耗智能点的功能可能无法继续使用，用户可以前往个人中心或平台指定页面购买或充值。"},
  {t: "h3", c: "4.3 套餐及充值"},
  {t: "p", c: "平台可能提供智能点充值、会员套餐、企业套餐或其他付费服务。"},
  {t: "p", c: "具体服务内容、价格、智能点额度、有效期、使用范围及其他规则，以用户购买时相关页面实际展示的信息为准。"},
  {t: "p", c: "用户在购买前应仔细确认套餐内容及相关规则。"},
  {t: "h3", c: "4.4 智能点消耗"},
  {t: "p", c: "智能点可能因AI模型调用、文件解析、内容生成、智能体运行、技能调用、第三方工具调用及其他需要计算资源的服务而产生消耗。"},
  {t: "p", c: "不同服务所需智能点可能存在差异，具体消耗情况以平台实际记录为准。"},
  {t: "p", c: "因模型类型、输入输出内容长度、文件大小、任务复杂程度等因素不同，同类任务实际消耗的智能点也可能有所不同。"},
  {t: "p", c: "用户可以通过平台提供的相关页面查看智能点余额及使用情况。"},
  {t: "h3", c: "4.5 价格调整"},
  {t: "lead", c: "平台有权根据模型成本、产品功能、市场情况等因素依法调整服务价格或智能点消耗标准。"},
  {t: "p", c: "对于已经购买的服务，我们将按照购买时约定的规则提供；涉及重大调整的，将通过合理方式向用户进行提示。"},
  {t: "h2", c: "五、人工智能服务特别约定"},
  {t: "h3", c: "5.1 AI生成内容"},
  {t: "lead", c: "云枢智能体生成的文字、图片、表格、文档、代码、分析结果及其他内容，可能由人工智能模型自动生成。"},
  {t: "p", c: "AI生成内容并不代表平台立场、观点或承诺。"},
  {t: "h3", c: "5.2 内容准确性"},
  {t: "lead", c: "受模型能力、训练数据、用户输入、知识时效性及第三方数据源等因素影响，AI可能出现“幻觉”、事实错误、逻辑错误或引用不存在的信息。"},
  {t: "p", c: "用户应对重要信息进行独立核实。"},
  {t: "h3", c: "5.3 AI内容标识"},
  {t: "lead", c: "对于依法需要进行人工智能生成内容标识的场景，平台将按照国家有关规定采取相应标识措施。"},
  {t: "p", c: "用户不得恶意删除、篡改、伪造依法设置的人工智能生成内容标识。"},
  {t: "h3", c: "5.4 用户输入内容"},
  {t: "lead", c: "用户应确保其向平台提交的提示词、文本、文件、图片、数据、企业资料及其他信息来源合法，并具有相应的使用、处理和授权权限。"},
  {t: "p", c: "用户不得上传或要求平台处理其无权使用的商业秘密、个人信息、国家秘密或其他受法律保护的信息。"},
  {t: "h3", c: "5.5 用户责任"},
  {t: "lead", c: "用户对其输入内容、使用方式以及AI生成结果的后续使用依法承担责任。"},
  {t: "h2", c: "六、文件、知识库及用户内容"},
  {t: "lead", c: "在使用云枢智能体过程中，您可能向平台上传文件、图片、文档、企业资料、知识库材料、模板等内容。"},
  {t: "p", c: "您保留依法享有的相关权利。"},
  {t: "p", c: "为了向您提供文件解析、知识检索、内容生成、智能体运行等服务，您授权平台在提供服务所必要的范围内对相关内容进行存储、复制、解析、格式转换、向量化、检索、计算和处理。"},
  {t: "p", c: "该授权仅以实现相关产品功能和提供服务为目的。"},
  {t: "p", c: "未经您的授权或其他合法依据，我们不会将您的非公开企业资料、私有知识库或私人上传文件用于与向您提供服务无关的公开用途。"},
  {t: "h2", c: "七、知识产权"},
  {t: "h3", c: "7.1 平台知识产权"},
  {t: "lead", c: "云枢智能体的软件、程序、界面、页面设计、商标、产品名称、技术方案、平台规则、系统架构及其他由平台依法享有权利的内容，其知识产权归平台或相关权利人所有。"},
  {t: "p", c: "未经书面许可，用户不得进行复制、修改、反向工程、出售、出租或用于其他侵犯平台合法权益的活动。"},
  {t: "h3", c: "7.2 用户输入内容"},
  {t: "lead", c: "用户上传或输入的内容，其原有知识产权归原权利人所有。"},
  {t: "p", c: "用户应确保拥有合法使用相关内容的权利。"},
  {t: "h3", c: "7.3 AI生成内容"},
  {t: "lead", c: "对于用户通过平台依法生成并可以依法享有权益的内容，平台不会仅因该内容通过云枢智能体生成而主张用户依法享有的相关权益。"},
  {t: "p", c: "但由于人工智能生成内容具有随机性，不同用户可能获得相同或相似的生成结果，平台无法保证生成内容具有唯一性、独创性，也无法保证生成结果不存在与第三方已有内容相似的情况。"},
  {t: "p", c: "对于拟公开发布、商业使用或用于重要项目的AI生成内容，用户应自行进行必要的知识产权审查。"},
  {t: "h2", c: "八、用户行为规范"},
  {t: "lead", c: "用户不得利用云枢智能体从事违反法律法规、损害国家安全、社会公共利益或他人合法权益的活动，包括但不限于："},
  {t: "p", c: "制作、复制、发布、传播违法违规信息；"},
  {t: "p", c: "侵犯他人知识产权、商业秘密、隐私权、个人信息权益或其他合法权益；"},
  {t: "p", c: "冒充他人身份、伪造事实、实施欺诈；"},
  {t: "p", c: "生成、传播虚假新闻或误导性信息；"},
  {t: "p", c: "利用平台实施网络攻击、恶意程序传播、数据窃取等行为；"},
  {t: "p", c: "未经授权获取、处理或披露他人数据；"},
  {t: "p", c: "恶意消耗平台计算资源、绕过平台计费或安全机制；"},
  {t: "p", c: "利用技术手段批量注册、恶意调用接口或影响平台正常运行；"},
  {t: "p", c: "其他违反法律法规、本协议或平台规则的行为。"},
  {t: "p", c: "如发现上述行为，平台有权根据具体情况采取警告、限制功能、停止任务、暂停服务、冻结账户或终止服务等措施。"},
  {t: "h2", c: "九、第三方服务"},
  {t: "lead", c: "为了提供完整的AI服务，云枢智能体可能接入第三方提供的："},
  {t: "p", c: "大语言模型；"},
  {t: "p", c: "云计算服务；"},
  {t: "p", c: "搜索服务；"},
  {t: "p", c: "OCR及文档解析服务；"},
  {t: "p", c: "数据接口；"},
  {t: "p", c: "开放API；"},
  {t: "p", c: "企业应用；"},
  {t: "p", c: "Open Connector类连接工具；"},
  {t: "p", c: "其他第三方软件或服务。"},
  {t: "p", c: "当用户主动选择或使用相关第三方服务时，部分必要信息可能需要提供给相应服务商进行处理。"},
  {t: "p", c: "平台将按照法律法规以及《云枢智能体隐私政策》的规定处理相关数据。"},
  {t: "p", c: "第三方独立提供的产品或服务由相应第三方承担责任。"},
  {t: "h2", c: "十、服务变更、中断与终止"},
  {t: "lead", c: "由于系统维护、升级、网络故障、模型提供商调整、第三方服务异常、不可抗力或其他合理原因，平台服务可能出现短暂中断或部分功能不可用。"},
  {t: "p", c: "平台将尽合理努力保障服务稳定。"},
  {t: "p", c: "平台可以根据业务发展情况对产品功能进行升级、调整或优化。"},
  {t: "p", c: "如果用户严重违反本协议或相关法律法规，平台有权依法限制或终止向其提供部分或全部服务。"},
  {t: "h2", c: "十一、责任限制"},
  {t: "lead", c: "在法律法规允许的范围内，对于因以下情况导致的服务异常或损失，平台将在合理范围内协助处理，但不对非因平台过错造成的损失承担责任："},
  {t: "p", c: "因用户自身操作不当造成的损失；"},
  {t: "p", c: "因用户未对AI生成结果进行必要核实导致的损失；"},
  {t: "p", c: "因用户设备、网络或第三方系统故障导致的问题；"},
  {t: "p", c: "因第三方模型、API、云服务等发生故障导致的服务异常；"},
  {t: "p", c: "因不可抗力造成的服务中断；"},
  {t: "p", c: "其他非因平台故意或重大过失造成的情形。"},
  {t: "p", c: "本条不影响用户依据法律法规依法享有的权利。"},
  {t: "h2", c: "十二、违约处理"},
  {t: "lead", c: "用户违反本协议、平台规则或法律法规的，平台可以根据行为性质及影响程度采取相应处理措施。"},
  {t: "p", c: "如用户行为导致平台或第三方遭受损失，用户应依法承担相应责任。"},
  {t: "p", c: "用户认为平台采取的处理措施存在错误的，可以通过客服渠道提出申诉。"},
  {t: "h2", c: "十三、未成年人保护"},
  {t: "lead", c: "云枢智能体主要面向具有完全民事行为能力的个人以及企业、机构用户。"},
  {t: "p", c: "未满18周岁的未成年人使用本服务，应当在父母或其他监护人的指导和同意下进行。"},
  {t: "p", c: "对于未满14周岁未成年人的个人信息，我们将按照法律法规关于敏感个人信息及未成年人个人信息保护的要求进行处理。"},
  {t: "h2", c: "十四、协议修改"},
  {t: "lead", c: "随着产品功能、服务模式以及法律法规发生变化，我们可能对本协议进行调整。"},
  {t: "p", c: "如修改涉及用户重要权益，我们将通过页面提示、弹窗、站内通知或其他合理方式向用户告知。"},
  {t: "p", c: "修改后的协议自公布或约定日期起生效。"},
  {t: "p", c: "如用户不同意修改后的协议，可以停止使用相关服务；继续使用服务的，视为按照依法有效的更新协议继续使用。"},
  {t: "h2", c: "十五、法律适用及争议解决"},
  {t: "lead", c: "本协议的订立、执行、解释及争议解决均适用中华人民共和国大陆地区法律。"},
  {t: "p", c: "因本协议或使用云枢智能体产生争议的，双方应首先友好协商解决。"},
  {t: "p", c: "协商不成的，任何一方可以依法向北京鼎函云枢科技有限公司所在地有管辖权的人民法院提起诉讼。"},
  {t: "p", c: "法律对消费者争议管辖另有强制性规定的，从其规定。"},
  {t: "h2", c: "十六、联系我们"},
  {t: "lead", c: "如您对本协议、账户、服务或其他事项存在疑问，可以通过以下方式联系我们："},
  {t: "contact", items: [
    {label: "运营主体：", value: "北京鼎函云枢科技有限公司"},
    {label: "联系地址：", value: "北京市朝阳区双井街道西大望路22号合生汇写字楼702室"},
    {label: "客服邮箱：", type: "email", value: "xuhuiquan@dinghanwang.com"},
    {label: "客服电话：", type: "phone", value: "13810362763"},
  ]},
  {t: "p", c: "我们将在收到您的反馈后依法及时处理。"},
  {t: "lead", strong: true, c: "特别提示：注册、登录或实际使用云枢智能体，即表示您已阅读、理解并同意本《云枢智能体用户服务协议》。"},
];

const PRIVACY_BLOCKS = [
  {t: "p", c: "北京鼎函云枢科技有限公司（以下简称“我们”）高度重视用户的隐私和个人信息保护。"},
  {t: "p", c: "本《云枢智能体隐私政策》适用于您访问、注册、登录及使用“云枢智能体”平台和相关产品、功能及服务过程中，我们对个人信息进行的处理活动。"},
  {t: "p", c: "我们将遵循合法、正当、必要和诚信原则，在实现明确、合理的处理目的所必要的范围内处理个人信息，并采取合理的安全措施保护您的个人信息。"},
  {t: "p", c: "请您在使用云枢智能体前仔细阅读本政策。"},
  {t: "h2", c: "一、我们如何收集和使用个人信息"},
  {t: "lead", c: "我们仅会在实现具体产品功能所必要的范围内处理您的信息。"},
  {t: "h3", c: "1. 注册及登录"},
  {t: "lead", c: "当您注册或登录云枢智能体时，我们可能收集："},
  {t: "p", c: "手机号码；"},
  {t: "p", c: "验证码；"},
  {t: "p", c: "第三方登录账户标识。"},
  {t: "p", c: "这些信息主要用于创建账户、身份验证、登录、安全管理和找回账户。"},
  {t: "h3", c: "2. 企业用户信息"},
  {t: "lead", c: "如果您通过企业或组织使用云枢智能体，我们可能处理："},
  {t: "p", c: "企业名称；"},
  {t: "p", c: "部门；"},
  {t: "p", c: "岗位或角色；"},
  {t: "p", c: "企业成员身份；"},
  {t: "p", c: "所属组织；"},
  {t: "p", c: "企业管理员配置的账户权限；"},
  {t: "p", c: "企业内部用户标识。"},
  {t: "p", c: "相关信息主要用于企业成员管理、组织管理、权限控制和企业服务。"},
  {t: "p", c: "企业管理员可能根据企业内部管理需要查看或管理成员的姓名、账户状态、角色权限、智能点使用情况及相关业务使用信息。"},
  {t: "h3", c: "3. 使用AI服务时产生的信息"},
  {t: "lead", c: "当您使用AI对话、智能体、技能或其他人工智能功能时，我们可能处理："},
  {t: "p", c: "您输入的文字；"},
  {t: "p", c: "提示词；"},
  {t: "p", c: "对话内容；"},
  {t: "p", c: "上传的文件；"},
  {t: "p", c: "图片；"},
  {t: "p", c: "文档；"},
  {t: "p", c: "表格；"},
  {t: "p", c: "知识库资料；"},
  {t: "p", c: "任务参数；"},
  {t: "p", c: "AI生成结果；"},
  {t: "p", c: "智能体运行记录；"},
  {t: "p", c: "技能及工具调用记录。"},
  {t: "p", c: "上述信息是实现AI理解、内容生成、文件分析、知识检索、任务执行和结果返回所必要的信息。"},
  {t: "p", c: "请您不要主动上传与当前任务无关的身份证号码、银行卡号、生物识别信息、医疗健康信息、精确位置等敏感个人信息。"},
  {t: "p", c: "如果确有业务需要，请确保您具有合法处理相关信息的依据。"},
  {t: "h2", c: "二、文件及知识库信息"},
  {t: "lead", c: "当您使用文件上传、企业资料库或知识库功能时，我们可能对您上传的资料进行："},
  {t: "p", c: "存储；"},
  {t: "p", c: "文本提取；"},
  {t: "p", c: "结构解析；"},
  {t: "p", c: "格式转换；"},
  {t: "p", c: "内容切分；"},
  {t: "p", c: "向量化处理；"},
  {t: "p", c: "索引；"},
  {t: "p", c: "检索；"},
  {t: "p", c: "AI推理。"},
  {t: "p", c: "上述处理仅用于实现相应知识库、文件分析和AI服务功能。"},
  {t: "p", c: "对于企业用户上传的内部资料，我们将按照企业账户权限及平台安全规则限制访问。"},
  {t: "p", c: "未经用户授权或其他合法依据，我们不会将用户的非公开企业文件、私人知识库内容用于与向用户提供服务无关的公开用途。"},
  {t: "h2", c: "三、设备及日志信息"},
  {t: "lead", c: "为了保障账户和平台安全、排查系统异常和改善服务质量，我们可能自动收集必要的设备及日志信息，例如："},
  {t: "p", c: "IP地址；"},
  {t: "p", c: "设备类型；"},
  {t: "p", c: "操作系统；"},
  {t: "p", c: "浏览器类型及版本；"},
  {t: "p", c: "登录时间；"},
  {t: "p", c: "访问时间；"},
  {t: "p", c: "操作日志；"},
  {t: "p", c: "服务调用记录；"},
  {t: "p", c: "错误日志；"},
  {t: "p", c: "网络状态；"},
  {t: "p", c: "安全事件记录。"},
  {t: "p", c: "此类信息主要用于身份验证、安全防护、故障排查、防止恶意攻击以及保障服务稳定运行。"},
  {t: "h2", c: "四、智能点及交易信息"},
  {t: "lead", c: "当您购买会员、套餐、智能点或其他付费服务时，我们可能处理："},
  {t: "p", c: "订单编号；"},
  {t: "p", c: "购买产品；"},
  {t: "p", c: "订单金额；"},
  {t: "p", c: "支付状态；"},
  {t: "p", c: "充值记录；"},
  {t: "p", c: "智能点余额；"},
  {t: "p", c: "智能点消耗记录；"},
  {t: "p", c: "开票信息。"},
  {t: "p", c: "实际支付通常可能由微信支付、支付宝、银行或其他第三方支付机构完成。"},
  {t: "p", c: "涉及支付账户、银行卡等信息的具体处理规则，以相应支付机构的隐私政策为准。"},
  {t: "h2", c: "五、客服及反馈信息"},
  {t: "lead", c: "当您联系我们、提交意见建议、问题反馈、投诉或申诉时，我们可能收集："},
  {t: "p", c: "联系方式；"},
  {t: "p", c: "问题描述；"},
  {t: "p", c: "截图；"},
  {t: "p", c: "附件；"},
  {t: "p", c: "沟通记录；"},
  {t: "p", c: "相关账户及任务信息。"},
  {t: "p", c: "上述信息用于核实问题、提供客服支持和处理投诉申诉。"},
  {t: "h2", c: "六、Cookies及类似技术"},
  {t: "lead", c: "为了保障网站正常运行、维持登录状态及改善用户体验，我们可能使用Cookie、本地存储或类似技术。"},
  {t: "p", c: "相关技术可能用于："},
  {t: "p", c: "保持登录状态；"},
  {t: "p", c: "保存用户偏好；"},
  {t: "p", c: "账户安全验证；"},
  {t: "p", c: "统计基本访问情况。"},
  {t: "p", c: "对于非提供基础功能所必需的信息处理，我们将依法采取必要的告知或授权措施。"},
  {t: "h2", c: "七、AI模型及第三方服务"},
  {t: "lead", c: "云枢智能体可能通过自研模型或接入第三方人工智能模型、云计算、搜索、数据API、文件解析及其他服务实现部分功能。"},
  {t: "p", c: "当您使用相关功能时，为完成任务，您的部分输入内容或任务参数可能需要传输至相应服务提供商进行处理。"},
  {t: "p", c: "例如可能包括："},
  {t: "p", c: "模型服务提供商；"},
  {t: "p", c: "云计算服务提供商；"},
  {t: "p", c: "搜索服务提供商；"},
  {t: "p", c: "OCR及文档解析服务商；"},
  {t: "p", c: "短信服务商；"},
  {t: "p", c: "支付服务商；"},
  {t: "p", c: "第三方开放API；"},
  {t: "p", c: "Open Connector等第三方连接服务。"},
  {t: "p", c: "我们将根据服务商在数据处理活动中的实际法律角色，通过委托处理、共同处理、对外提供个人信息或其他符合法律规定的方式进行管理，并依法签署相关数据保护协议或采取其他必要措施。"},
  {t: "p", c: "涉及依法需要取得单独同意的，我们将在实际发生相关处理活动前另行取得您的同意。"},
  {t: "p", c: "平台上线时，我们将根据实际接入情况通过《第三方信息共享清单》或类似页面向您说明涉及的第三方服务名称、处理目的和信息类型。"},
  {t: "h2", c: "八、我们如何使用AI对话数据"},
  {t: "lead", c: "用户输入内容和AI生成结果主要用于："},
  {t: "p", c: "响应用户提出的问题；"},
  {t: "p", c: "执行用户创建的任务；"},
  {t: "p", c: "运行智能体或技能；"},
  {t: "p", c: "检索用户知识库；"},
  {t: "p", c: "提供上下文连续对话；"},
  {t: "p", c: "保障系统安全；"},
  {t: "p", c: "排查服务故障；"},
  {t: "p", c: "依法处理违规内容。"},
  {t: "p", c: "除法律法规允许或您另行明确授权的情况外，我们不会将您的非公开个人对话、企业私有文件或私人知识库内容用于与提供服务无关的公开用途。"},
  {t: "p", c: "如果未来涉及将相关个人信息用于与原处理目的不直接相关的模型训练、产品研究或其他用途，我们将按照法律法规要求另行告知并在必要时取得您的同意。"},
  {t: "h2", c: "九、信息共享、转让和公开披露"},
  {t: "lead", c: "我们不会随意向第三方出售您的个人信息。"},
  {t: "p", c: "仅在以下情况下，我们可能依法向第三方提供相关信息："},
  {t: "p", c: "取得您的授权或同意；"},
  {t: "p", c: "为履行您作为一方当事人的合同所必需；"},
  {t: "p", c: "依据法律法规或履行法定义务所必需；"},
  {t: "p", c: "应司法机关、行政机关依法提出的要求；"},
  {t: "p", c: "为了保护您或其他自然人的生命健康和财产安全，在紧急情况下依法处理；"},
  {t: "p", c: "法律法规规定的其他情形。"},
  {t: "p", c: "如果发生合并、收购、资产转让、重组等导致个人信息处理者发生变化的情况，我们将依法向您告知相关情况，并要求新的个人信息处理者继续履行本隐私政策以及法律规定的个人信息保护义务。"},
  {t: "h2", c: "十、敏感个人信息"},
  {t: "lead", c: "敏感个人信息一旦泄露或者被非法使用，容易导致自然人的人格尊严受到侵害或人身、财产安全受到危害。"},
  {t: "p", c: "原则上，云枢智能体并不要求用户提交与服务无关的敏感个人信息。"},
  {t: "p", c: "如特定功能确需处理敏感个人信息，我们将依法向您说明处理敏感个人信息的必要性及对个人权益的影响，并在依法需要的情况下取得您的单独同意。"},
  {t: "h2", c: "十一、个人信息的保存"},
  {t: "lead", c: "我们仅在实现本政策所述处理目的所必要的最短期限内保存个人信息，法律法规另有规定的除外。"},
  {t: "p", c: "不同类型数据的具体保存时间可能因服务类型、安全要求、法律义务及企业客户配置不同而有所不同。"},
  {t: "p", c: "当个人信息保存期限届满后，我们将依法删除或进行匿名化处理，法律法规要求继续保存的除外。"},
  {t: "p", c: "账户注销后，我们将根据法律法规及业务实际依法删除或匿名化处理相关个人信息。"},
  {t: "h2", c: "十二、个人信息存储及跨境处理"},
  {t: "lead", c: "我们将按照中国法律法规关于数据存储和跨境传输的规定处理相关数据。"},
  {t: "p", c: "如相关数据在中华人民共和国境内收集和产生并依法应当在境内存储，我们将按照法律规定进行存储。"},
  {t: "p", c: "如因使用境外模型、云服务或其他业务原因确需向中华人民共和国境外提供个人信息，我们将按照法律法规履行相应义务，并在依法需要的情况下向您告知境外接收方相关信息并取得单独同意。"},
  {t: "p", strong: true, c: "【平台上线前应根据实际服务器部署位置及所调用的大模型服务确认本条具体情况。】"},
  {t: "h2", c: "十三、我们如何保护您的信息"},
  {t: "lead", c: "我们将根据业务情况采取合理可行的安全措施保护个人信息，包括："},
  {t: "p", c: "访问权限控制；"},
  {t: "p", c: "身份认证；"},
  {t: "p", c: "数据传输保护；"},
  {t: "p", c: "数据备份；"},
  {t: "p", c: "日志审计；"},
  {t: "p", c: "安全监测；"},
  {t: "p", c: "漏洞管理；"},
  {t: "p", c: "账户异常检测；"},
  {t: "p", c: "员工权限管理；"},
  {t: "p", c: "数据安全事件应急机制。"},
  {t: "p", c: "尽管我们会采取合理措施保护您的信息，但互联网环境不存在绝对安全。"},
  {t: "p", c: "如发生或者可能发生个人信息泄露、篡改、丢失等安全事件，我们将按照法律法规采取补救措施，并依法履行通知、报告等义务。"},
  {t: "h2", c: "十四、您的个人信息权利"},
  {t: "lead", c: "在符合法律法规规定的情况下，您对自己的个人信息享有相关权利，包括："},
  {t: "p", c: "查阅；"},
  {t: "p", c: "复制；"},
  {t: "p", c: "更正；"},
  {t: "p", c: "补充；"},
  {t: "p", c: "删除；"},
  {t: "p", c: "撤回同意；"},
  {t: "p", c: "限制或拒绝特定个人信息处理；"},
  {t: "p", c: "注销账户；"},
  {t: "p", c: "要求解释个人信息处理规则。"},
  {t: "p", c: "您可以通过平台提供的个人中心、账户设置、客服或本政策公布的联系方式提出相关请求。"},
  {t: "p", c: "为了保障账户安全，我们可能需要在处理相关请求前核实您的身份。"},
  {t: "p", c: "对于您的合理请求，我们将在法律法规规定的期限内处理。"},
  {t: "h2", c: "十五、账号注销"},
  {t: "lead", c: "您可以通过平台提供的账户注销入口申请注销账户。"},
  {t: "p", c: "如平台暂未提供自助注销入口，可以通过本政策公布的客服渠道联系我们。"},
  {t: "p", c: "账号注销后，您将无法继续使用相关账户及部分服务，与账户相关的数据将按照法律法规和平台规则进行删除、匿名化或依法保存。"},
  {t: "p", c: "注销前请自行备份需要保留的文件、AI生成内容及其他重要数据。"},
  {t: "p", c: "企业成员账户可能受到所属企业的统一管理。如果企业用户账户涉及企业资料和权限，应根据企业管理关系处理。"},
  {t: "h2", c: "十六、企业用户特别说明"},
  {t: "lead", c: "如果您通过所在企业、机构或其他组织获得云枢智能体账户，相关企业可能决定员工账户信息及业务数据的处理目的和方式。"},
  {t: "p", c: "在此情况下，对于由企业决定处理的个人信息，相关企业可能属于法律规定的个人信息处理者，我们根据企业委托提供相应技术服务。"},
  {t: "p", c: "企业管理员可能根据企业内部规则管理："},
  {t: "p", c: "员工账户；"},
  {t: "p", c: "部门；"},
  {t: "p", c: "角色；"},
  {t: "p", c: "权限；"},
  {t: "p", c: "智能点额度；"},
  {t: "p", c: "企业知识库；"},
  {t: "p", c: "企业智能体；"},
  {t: "p", c: "服务使用情况。"},
  {t: "p", c: "如您对所在企业如何使用相关信息存在疑问，可以向企业管理员或企业个人信息保护负责人咨询。"},
  {t: "h2", c: "十七、未成年人信息保护"},
  {t: "lead", c: "云枢智能体主要面向成年人以及企业、机构用户。"},
  {t: "p", c: "未满18周岁的未成年人应当在父母或其他监护人指导和同意下使用本服务。"},
  {t: "p", c: "如我们发现未经监护人同意收集了未满14周岁未成年人的个人信息，将依法采取删除或其他必要措施。"},
  {t: "p", c: "如产品后续专门向未满14周岁未成年人提供服务，我们将依法制定专门的个人信息处理规则。"},
  {t: "h2", c: "十八、无需征得同意的情形"},
  {t: "lead", c: "根据法律法规，在符合规定的情况下，处理个人信息可能无需取得个人同意，例如："},
  {t: "p", c: "为订立、履行个人作为一方当事人的合同所必需；"},
  {t: "p", c: "为履行法定职责或法定义务所必需；"},
  {t: "p", c: "为应对突发公共卫生事件，或者紧急情况下为保护自然人的生命健康和财产安全所必需；"},
  {t: "p", c: "在合理范围内处理个人自行公开或者其他已经合法公开的个人信息；"},
  {t: "p", c: "法律法规规定的其他情形。"},
  {t: "h2", c: "十九、本政策的更新"},
  {t: "lead", c: "随着产品功能、业务模式或法律法规发生变化，我们可能适时更新本隐私政策。"},
  {t: "p", c: "对于可能对您的个人信息权益产生重大影响的更新，我们将通过平台公告、弹窗、站内消息或其他适当方式向您提示。"},
  {t: "p", c: "未经法律允许，我们不会通过更新本政策降低您依法享有的个人信息权益。"},
  {t: "h2", c: "二十、联系我们"},
  {t: "lead", c: "如果您对本隐私政策、个人信息处理活动或个人信息权利存在疑问、意见、投诉或请求，可以联系我们："},
  {t: "contact", items: [
    {label: "个人信息处理者：", value: "北京鼎函云枢科技有限公司"},
    {label: "注册地址：", value: "北京市朝阳区双井街道西大望路22号合生汇写字楼702室"},
    {label: "客服邮箱：", type: "email", value: "xuhuiquan@dinghanwang.com"},
    {label: "客服电话：", type: "phone", value: "13810362763"},
    {label: "个人信息保护联系邮箱：", type: "email", value: "xuhuiquan@dinghanwang.com"},
  ]},
  {t: "p", c: "我们将在收到您的请求后依法及时进行核实和处理。"},
  {t: "lead", strong: true, c: "使用云枢智能体前，请您仔细阅读并充分理解本《云枢智能体隐私政策》。"},
];

export function TermsContent() {
  return <div className="pa-agreement">{renderBlocks(TERMS_BLOCKS)}</div>;
}

export function PrivacyContent() {
  return <div className="pa-agreement">{renderBlocks(PRIVACY_BLOCKS)}</div>;
}
